import { forwardRef, type ForwardRefExoticComponent, type RefAttributes } from 'react'
import { HugeiconsIcon, type HugeiconsIconProps, type IconSvgElement } from '@hugeicons/react'
import * as coreIcons from '@hugeicons/core-free-icons'

export type LucideProps = Omit<HugeiconsIconProps, "icon" | "altIcon">
export type LucideIcon = ForwardRefExoticComponent<LucideProps & RefAttributes<SVGSVGElement>>

const FALLBACK_ICON = (coreIcons.CircleIcon ?? coreIcons.SquareIcon) as IconSvgElement
const iconEntries = Object.entries(coreIcons) as Array<[string, IconSvgElement]>
const iconOverrides: Record<string, keyof typeof coreIcons> = {
  X: 'Cancel01Icon',
  Plus: 'Add01Icon',
  PanelLeft: 'PanelLeftCloseIcon',
  PanelRightClose: 'PanelRightCloseIcon',
  PanelLeftClose: 'PanelLeftCloseIcon',
}

function toTokens(name: string): string[] {
  return name
    .replace(/Icon$/, "")
    .replace(/\d+/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean)
}

function scoreCandidate(iconName: string, targetTokens: string[]): number {
  const normalized = iconName.toLowerCase()
  let score = 0
  for (const token of targetTokens) {
    if (normalized.includes(token)) score += 2
  }
  if (targetTokens.length > 0 && normalized.startsWith(targetTokens[0])) score += 1
  if (/0\dicon$/i.test(iconName)) score += 0.5
  return score
}

function resolveHugeIcon(iconName: string): IconSvgElement {
  const overrideKey = iconOverrides[iconName]
  if (overrideKey && overrideKey in coreIcons) {
    return coreIcons[overrideKey] as IconSvgElement
  }

  const directKey = `${iconName}Icon` as keyof typeof coreIcons
  if (directKey in coreIcons) return coreIcons[directKey] as IconSvgElement

  const tokens = toTokens(iconName)
  let best: IconSvgElement | null = null
  let bestScore = -1

  for (const [key, value] of iconEntries) {
    const score = scoreCandidate(key, tokens)
    if (score > bestScore) {
      bestScore = score
      best = value
    }
  }

  return bestScore >= 2 ? (best as IconSvgElement) : FALLBACK_ICON
}

function createIconComponent(iconName: string): LucideIcon {
  const icon = resolveHugeIcon(iconName)

  const Component = forwardRef<SVGSVGElement, LucideProps>((props, ref) => (
    <HugeiconsIcon ref={ref} icon={icon} {...props} />
  ))

  Component.displayName = iconName
  return Component
}

export const Activity = createIconComponent('Activity')
export const AlertCircle = createIconComponent('AlertCircle')
export const AlertTriangle = createIconComponent('AlertTriangle')
export const ArrowDown = createIconComponent('ArrowDown')
export const ArrowLeft = createIconComponent('ArrowLeft')
export const ArrowRight = createIconComponent('ArrowRight')
export const ArrowUp = createIconComponent('ArrowUp')
export const Archive02 = createIconComponent('Archive02')
export const Backpack = createIconComponent('Backpack')
export const Battery = createIconComponent('Battery')
export const Bell = createIconComponent('Bell')
export const BookOpen = createIconComponent('BookOpen')
export const Bot = createIconComponent('Bot')
export const Box = createIconComponent('Box')
export const Calendar = createIconComponent('Calendar')
export const CalendarDays = createIconComponent('CalendarDays')
export const CalendarIcon = createIconComponent('CalendarIcon')
export const Camera = createIconComponent('Camera')
export const Car = createIconComponent('Car')
export const Check = createIconComponent('Check')
export const CheckCheck = createIconComponent('CheckCheck')
export const CheckCircle = createIconComponent('CheckCircle')
export const CheckCircle2 = createIconComponent('CheckCircle2')
export const CheckSquare = createIconComponent('CheckSquare')
export const ChevronDown = createIconComponent('ChevronDown')
export const ChevronDownIcon = createIconComponent('ChevronDownIcon')
export const ChevronLeft = createIconComponent('ChevronLeft')
export const ChevronLeftIcon = createIconComponent('ChevronLeftIcon')
export const ChevronRight = createIconComponent('ChevronRight')
export const ChevronRightIcon = createIconComponent('ChevronRightIcon')
export const ChevronUp = createIconComponent('ChevronUp')
export const Circle = createIconComponent('Circle')
export const CircleDot = createIconComponent('CircleDot')
export const ClipboardCheck = createIconComponent('ClipboardCheck')
export const Clock = createIconComponent('Clock')
export const Cloud = createIconComponent('Cloud')
export const CloudRain = createIconComponent('CloudRain')
export const CloudSnow = createIconComponent('CloudSnow')
export const CloudSun = createIconComponent('CloudSun')
export const Code = createIconComponent('Code')
export const Coffee = createIconComponent('Coffee')
export const Compass = createIconComponent('Compass')
export const Copy = createIconComponent('Copy')
export const CornerDownLeft = createIconComponent('CornerDownLeft')
export const Crown = createIconComponent('Crown')
export const DollarSign = createIconComponent('DollarSign')
export const Droplet = createIconComponent('Droplet')
export const Edit = createIconComponent('Edit')
export const Edit2 = createIconComponent('Edit2')
export const ExternalLink = createIconComponent('ExternalLink')
export const Eye = createIconComponent('Eye')
export const EyeOff = createIconComponent('EyeOff')
export const Facebook = createIconComponent('Facebook')
export const FileText = createIconComponent('FileText')
export const FileUp = createIconComponent('FileUp')
export const Filter = createIconComponent('Filter')
export const Flag = createIconComponent('Flag')
export const Flame = createIconComponent('Flame')
export const Folder = createIconComponent('Folder')
export const FolderOpen = createIconComponent('FolderOpen')
export const FolderPlus = createIconComponent('FolderPlus')
export const Fuel = createIconComponent('Fuel')
export const Globe = createIconComponent('Globe')
export const GripVertical = createIconComponent('GripVertical')
export const Heart = createIconComponent('Heart')
export const HelpCircle = createIconComponent('HelpCircle')
export const Home = createIconComponent('Home')
export const Info = createIconComponent('Info')
export const Instagram = createIconComponent('Instagram')
export const LayoutGrid = createIconComponent('LayoutGrid')
export const Leaf = createIconComponent('Leaf')
export const Lightbulb = createIconComponent('Lightbulb')
export const Link2 = createIconComponent('Link2')
export const List = createIconComponent('List')
export const ListChecks = createIconComponent('ListChecks')
export const Loader2 = createIconComponent('Loader2')
export const Lock = createIconComponent('Lock')
export const LogOut = createIconComponent('LogOut')
export const Mail = createIconComponent('Mail')
export const Maximize2 = createIconComponent('Maximize2')
export const Map = createIconComponent('Map')
export const MapPin = createIconComponent('MapPin')
export const Megaphone = createIconComponent('Megaphone')
export const Menu = createIconComponent('Menu')
export const MessageCircle = createIconComponent('MessageCircle')
export const MessageSquare = createIconComponent('MessageSquare')
export const Minus = createIconComponent('Minus')
export const Monitor = createIconComponent('Monitor')
export const CornerDownRight = createIconComponent('CornerDownRight')
export const Moon = createIconComponent('Moon')
export const MoreHorizontal = createIconComponent('MoreHorizontal')
export const MoreVertical = createIconComponent('MoreVertical')
export const Mountain = createIconComponent('Mountain')
export const MousePointer = createIconComponent('MousePointer')
export const MoveRight = createIconComponent('MoveRight')
export const Music2 = createIconComponent('Music2')
export const Navigation = createIconComponent('Navigation')
export const Package = createIconComponent('Package')
export const Palette = createIconComponent('Palette')
export const PanelLeft = createIconComponent('PanelLeft')
export const PanelLeftClose = createIconComponent('PanelLeftClose')
export const PanelRightClose = createIconComponent('PanelRightClose')
export const Pencil = createIconComponent('Pencil')
export const Phone = createIconComponent('Phone')
export const PhoneCall = createIconComponent('PhoneCall')
export const Pin = createIconComponent('Pin')
export const PinOff = createIconComponent('PinOff')
export const Plus = createIconComponent('Plus')
export const Radio = createIconComponent('Radio')
export const RefreshCw = createIconComponent('RefreshCw')
export const Route = createIconComponent('Route')
export const Save = createIconComponent('Save')
export const Search = createIconComponent('Search')
export const Send = createIconComponent('Send')
export const Settings = createIconComponent('Settings')
export const Share2 = createIconComponent('Share2')
export const Shield = createIconComponent('Shield')
export const ShieldCheck = createIconComponent('ShieldCheck')
export const Smartphone = createIconComponent('Smartphone')
export const Sparkles = createIconComponent('Sparkles')
export const Square = createIconComponent('Square')
export const Star = createIconComponent('Star')
export const Sun = createIconComponent('Sun')
export const Tag = createIconComponent('Tag')
export const Target = createIconComponent('Target')
export const Tent = createIconComponent('Tent')
export const ThumbsUp = createIconComponent('ThumbsUp')
export const Trash2 = createIconComponent('Trash2')
export const TreePine = createIconComponent('TreePine')
export const Trees = createIconComponent('Trees')
export const TrendingUp = createIconComponent('TrendingUp')
export const Truck = createIconComponent('Truck')
export const Undo2 = createIconComponent('Undo2')
export const Upload = createIconComponent('Upload')
export const UploadCloud = createIconComponent('UploadCloud')
export const User = createIconComponent('User')
export const UserMinus = createIconComponent('UserMinus')
export const UserPlus = createIconComponent('UserPlus')
export const Users = createIconComponent('Users')
export const Utensils = createIconComponent('Utensils')
export const UtensilsCrossed = createIconComponent('UtensilsCrossed')
export const Waves = createIconComponent('Waves')
export const Wind = createIconComponent('Wind')
export const Wrench = createIconComponent('Wrench')
export const X = createIconComponent('X')
export const XCircle = createIconComponent('XCircle')
export const Youtube = createIconComponent('Youtube')
export const Zap = createIconComponent('Zap')
export const Download = createIconComponent('Download')
export const Film = createIconComponent('Film')

export default {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Archive02,
  Backpack,
  Battery,
  Bell,
  BookOpen,
  Bot,
  Box,
  Calendar,
  CalendarDays,
  CalendarIcon,
  Camera,
  Car,
  Check,
  CheckCheck,
  CheckCircle,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronDownIcon,
  ChevronLeft,
  ChevronLeftIcon,
  ChevronRight,
  ChevronRightIcon,
  ChevronUp,
  Circle,
  CircleDot,
  ClipboardCheck,
  Clock,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudSun,
  Code,
  Coffee,
  Compass,
  Copy,
  CornerDownLeft,
  CornerDownRight,
  Crown,
  DollarSign,
  Droplet,
  Edit,
  Edit2,
  ExternalLink,
  Eye,
  EyeOff,
  Facebook,
  FileText,
  FileUp,
  Filter,
  Flag,
  Flame,
  Folder,
  FolderOpen,
  FolderPlus,
  Fuel,
  Globe,
  GripVertical,
  Heart,
  HelpCircle,
  Home,
  Info,
  Instagram,
  LayoutGrid,
  Leaf,
  Lightbulb,
  Link2,
  List,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Maximize2,
  Map,
  MapPin,
  Megaphone,
  Menu,
  MessageCircle,
  MessageSquare,
  Minus,
  Monitor,
  Moon,
  MoreHorizontal,
  MoreVertical,
  Mountain,
  MousePointer,
  MoveRight,
  Music2,
  Navigation,
  Package,
  Palette,
  PanelLeft,
  PanelLeftClose,
  PanelRightClose,
  Pencil,
  Phone,
  PhoneCall,
  Pin,
  PinOff,
  Plus,
  Radio,
  RefreshCw,
  Route,
  Save,
  Search,
  Send,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Square,
  Star,
  Sun,
  Tag,
  Target,
  Tent,
  ThumbsUp,
  Trash2,
  TreePine,
  Trees,
  TrendingUp,
  Truck,
  Undo2,
  Upload,
  UploadCloud,
  User,
  UserMinus,
  UserPlus,
  Users,
  Utensils,
  UtensilsCrossed,
  Waves,
  Wind,
  Wrench,
  X,
  XCircle,
  Youtube,
  Zap,
}
